/**
 * Supabase health monitor — detects outages and auto-recovers.
 * Plain TypeScript module (no React dependency) with event-based updates.
 */

export type HealthStatus = 'healthy' | 'degraded' | 'down';

type Listener = (status: HealthStatus) => void;

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;
const HEALTH_ENDPOINT = `${SUPABASE_URL}/auth/v1/health`;
const HEALTH_TIMEOUT_MS = 5_000;
const POLL_HEALTHY_MS = 60_000;
const POLL_UNHEALTHY_MS = 15_000;

class SupabaseHealthMonitor {
  private status: HealthStatus = 'healthy';
  private consecutiveFailures = 0;
  private listeners = new Set<Listener>();
  private timerId: ReturnType<typeof setTimeout> | null = null;
  private started = false;

  /** Subscribe to status changes. Returns an unsubscribe function. */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    this.ensureStarted();
    return () => {
      this.listeners.delete(listener);
      if (this.listeners.size === 0) this.stop();
    };
  }

  getStatus(): HealthStatus {
    return this.status;
  }

  /**
   * Trigger an immediate health check. When called from a known timeout error,
   * count the caller's failure so only one health-check failure is needed to reach 'down'.
   */
  forceCheck(): void {
    this.ensureStarted();
    // The caller already experienced a failure — count it
    this.consecutiveFailures = Math.max(this.consecutiveFailures, 1);
    this.check();
  }

  private ensureStarted() {
    if (this.started) return;
    this.started = true;
    document.addEventListener('visibilitychange', this.handleVisibility);
    // Immediate first check instead of waiting 60s
    this.check();
  }

  private stop() {
    this.started = false;
    if (this.timerId) clearTimeout(this.timerId);
    this.timerId = null;
    document.removeEventListener('visibilitychange', this.handleVisibility);
  }

  private handleVisibility = () => {
    if (document.hidden) {
      // Pause polling when tab is in background
      if (this.timerId) clearTimeout(this.timerId);
      this.timerId = null;
    } else {
      // Tab became visible — check immediately then resume schedule
      this.check();
    }
  };

  private scheduleNext() {
    if (this.timerId) clearTimeout(this.timerId);
    const interval = this.status === 'healthy' ? POLL_HEALTHY_MS : POLL_UNHEALTHY_MS;
    this.timerId = setTimeout(() => this.check(), interval);
  }

  private async check() {
    if (this.timerId) clearTimeout(this.timerId);
    this.timerId = null;

    try {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), HEALTH_TIMEOUT_MS);

      const response = await fetch(HEALTH_ENDPOINT, {
        method: 'GET',
        signal: controller.signal,
        headers: {
          'apikey': import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string,
        },
      });
      clearTimeout(timer);

      if (response.ok) {
        this.consecutiveFailures = 0;
        this.setStatus('healthy');
      } else {
        this.recordFailure();
      }
    } catch {
      this.recordFailure();
    }

    if (this.started && !document.hidden) {
      this.scheduleNext();
    }
  }

  private recordFailure() {
    this.consecutiveFailures++;
    if (this.consecutiveFailures >= 2) {
      this.setStatus('down');
    } else {
      this.setStatus('degraded');
    }
  }

  private setStatus(newStatus: HealthStatus) {
    if (newStatus === this.status) return;
    this.status = newStatus;
    this.listeners.forEach((fn) => fn(newStatus));
  }
}

export const supabaseHealth = new SupabaseHealthMonitor();
